import { Flex, Loader, Text, TextInput } from "@mantine/core";
import { Activity, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { cn } from "../../../lib/utils";
import { useSignalDocuments } from "../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import type { SignalDocument } from "../../../services/signalDocuments/signalDocumentsEndpoints.interface";
import type { GroupedAccount, HealthStatusKey } from "./healthTypes";
import {
  HEALTH_STATUS_STYLES,
  COLUMN_CONFIG,
  extractHealthInfo,
} from "./healthTypes";
import HealthKanbanColumn from "./components/HealthKanbanColumn";
import HealthDetailSheet from "./components/HealthDetailSheet";
import HealthModelFilter from "./components/HealthModelFilter";

const HealthPage = () => {
  const { workspaceId } = useParams();
  const currentWorkspace = useWorkspaceStore((state) => state.workspace);
  const wsId = currentWorkspace?.id || workspaceId || "";
  const workspaceSlug = currentWorkspace?.unique_identifier || workspaceId || "";

  const [selectedModelKey, setSelectedModelKey] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAccount, setSelectedAccount] = useState<GroupedAccount | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [columnPages, setColumnPages] = useState<Record<HealthStatusKey, number>>({
    healthy: 1,
    at_risk: 1,
    critical: 1,
  });

  const { data: documents, isLoading } = useSignalDocuments({
    workspaceId: wsId,
    signalType: "health",
    healthModelKey: selectedModelKey !== "all" ? selectedModelKey : undefined,
    enabled: !!wsId,
  }) as { data: SignalDocument[] | undefined; isLoading: boolean };

  // Group documents by account
  const grouped: GroupedAccount[] = useMemo(() => {
    if (!documents?.length) return [];

    // Debug: log first document to inspect structured_result shape
    if (documents[0]) {
      console.log("[HealthPage] Sample document fields:", {
        id: documents[0].id,
        structured_result: documents[0].structured_result,
        content_structured: documents[0].content_structured,
        health_model_key: documents[0].health_model_key,
        content_markdown_preview: documents[0].content_markdown?.slice(0, 300),
      });
    }

    const map = new Map<string, GroupedAccount>();

    for (const doc of documents) {
      const entityId = doc.entity_external_id || "unknown";
      if (!map.has(entityId)) {
        map.set(entityId, {
          entityId,
          accountName: entityId,
          latestStatus: null,
          latestDate: null,
          latestSummary: null,
          documents: [],
        });
      }
      const group = map.get(entityId)!;
      group.documents.push(doc);
    }

    for (const group of map.values()) {
      group.documents.sort(
        (a, b) =>
          new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime(),
      );
      const latestDoc = group.documents[0];
      const healthInfo = extractHealthInfo(latestDoc);
      group.latestStatus = healthInfo?.health_status || null;
      group.latestSummary = healthInfo?.summary || null;
      group.latestDate = latestDoc?.created_at || null;
      group.accountName = latestDoc?.entity_name || group.entityId;
    }

    return Array.from(map.values());
  }, [documents]);

  // Split into columns and apply search filter
  const columns = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = q
      ? grouped.filter(
          (g) =>
            g.accountName.toLowerCase().includes(q) ||
            g.entityId.toLowerCase().includes(q),
        )
      : grouped;

    const result: Record<HealthStatusKey, GroupedAccount[]> = {
      healthy: [],
      at_risk: [],
      critical: [],
    };

    for (const account of filtered) {
      const status = account.latestStatus as HealthStatusKey;
      if (status in result) {
        result[status].push(account);
      }
    }

    return result;
  }, [grouped, searchQuery]);

  // Status counts (unfiltered)
  const statusCounts = useMemo(() => {
    const counts = { healthy: 0, at_risk: 0, critical: 0, total: grouped.length };
    for (const g of grouped) {
      if (g.latestStatus === "healthy") counts.healthy++;
      else if (g.latestStatus === "at_risk") counts.at_risk++;
      else if (g.latestStatus === "critical") counts.critical++;
    }
    return counts;
  }, [grouped]);

  const handleCardClick = (account: GroupedAccount) => {
    setSelectedAccount(account);
    setDrawerOpen(true);
  };

  const handlePageChange = (key: HealthStatusKey, page: number) => {
    setColumnPages((prev) => ({ ...prev, [key]: page }));
  };

  // Reset pagination when filters change
  const handleModelChange = (value: string) => {
    setSelectedModelKey(value);
    setColumnPages({ healthy: 1, at_risk: 1, critical: 1 });
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setColumnPages({ healthy: 1, at_risk: 1, critical: 1 });
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader size="sm" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col px-6 py-6">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Activity className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-semibold text-foreground">Account Health</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Health scoring documents attached to accounts, generated by your health automation.
        </p>
      </div>

      {/* Filters row */}
      <div className="mb-4 flex items-center gap-3">
        <HealthModelFilter
          workspaceId={wsId}
          value={selectedModelKey}
          onChange={handleModelChange}
        />
        <TextInput
          size="xs"
          w={220}
          placeholder="Search accounts..."
          leftSection={<Search className="h-3.5 w-3.5 text-muted-foreground" />}
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.currentTarget.value)}
        />
        <Text size="xs" c="dimmed">
          {grouped.length} account{grouped.length !== 1 ? "s" : ""}
        </Text>
      </div>

      {/* Summary cards */}
      {grouped.length > 0 && (
        <div className="mb-4 grid grid-cols-4 gap-3">
          <SummaryCard label="Total Accounts" value={statusCounts.total} />
          <SummaryCard label="Healthy" value={statusCounts.healthy} dotColor="bg-emerald-500" />
          <SummaryCard label="At Risk" value={statusCounts.at_risk} dotColor="bg-amber-500" />
          <SummaryCard label="Critical" value={statusCounts.critical} dotColor="bg-rose-500" />
        </div>
      )}

      {/* Kanban columns */}
      {grouped.length === 0 ? (
        <Flex justify="center" py="xl" className="flex-1">
          <Text size="sm" c="dimmed">
            No health documents generated yet. Configure and run a health automation to see results here.
          </Text>
        </Flex>
      ) : (
        <div className="flex flex-1 gap-4 overflow-hidden">
          {COLUMN_CONFIG.map((col) => (
            <HealthKanbanColumn
              key={col.key}
              label={col.label}
              dotColor={col.dotColor}
              accounts={columns[col.key]}
              statusStyles={HEALTH_STATUS_STYLES}
              page={columnPages[col.key]}
              onPageChange={(page) => handlePageChange(col.key, page)}
              onCardClick={handleCardClick}
            />
          ))}
        </div>
      )}

      {/* Detail drawer */}
      <HealthDetailSheet
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        account={selectedAccount}
        statusStyles={HEALTH_STATUS_STYLES}
        workspaceId={workspaceSlug}
      />
    </div>
  );
};

/* ---------- Sub-components ---------- */

const SummaryCard = ({
  label,
  value,
  dotColor,
}: {
  label: string;
  value: number;
  dotColor?: string;
}) => (
  <div className="rounded-lg border border-border bg-card px-4 py-3">
    <div className="flex items-center gap-2">
      {dotColor && <span className={cn("h-2 w-2 rounded-full", dotColor)} />}
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </div>
    <Text size="lg" fw={600} mt={4}>
      {value}
    </Text>
  </div>
);

export default HealthPage;
